import React, { useState, useEffect, useMemo } from 'react';
import {
  Autocomplete,
  Box,
  Paper,
  Typography,
  TextField,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Card,
  CardContent,
  Stack,
  Chip,
  Alert,
  CircularProgress,
  Divider,
  IconButton,
  TablePagination,
  Tooltip
} from '@mui/material';
import {
  Download,
  CalendarToday,
  AccessTime,
  Person,
  ArrowBack,
  Favorite,
  CloudUpload,
  PictureAsPdf
} from '@mui/icons-material';
import { format, parse, lastDayOfMonth } from 'date-fns';
import { useNavigate } from 'react-router-dom';
import { billingApi, patientApi } from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import { useOrganization } from '../contexts/OrganizationContext';
import { useCustomViews, useViewChoice, STANDARD } from './customViews/useCustomViews';
import ViewSelector from './customViews/ViewSelector';
import CustomBillingView from './customViews/CustomBillingView';
import BillingReportsPanel from './reports/BillingReportsPanel';
import {
  generateBillingReportPdf,
  billingReportPdfFilename,
  BillingReportPdfActivity,
} from '../utils/generateBillingReportPdf';

// Backend response format (from real server)
interface BackendReportRecord {
  Month: string;
  patient_id: string;
  first_name: string;
  last_name: string;
  date_of_birth: string;
  consent_date: string;
  care_plan_created_date: string;
  icd10_codes: string;
  cpt_codes: string;
  'spent (minutes)': number;
  actions: number;
  users: number;
  apcm_tier?: number | null;
  apcm_default_code?: string | null;
  program?: string;
  // PHA-629: patient-side engagement signals. Computed by PHA-404 and returned by the
  // backend since then, but previously undeclared here so TS silently dropped them.
  // These are the same two signals that drive has_activity in calculate_all_billing_codes,
  // so they explain why an APCM code fired on a patient with no staff time.
  patient_survey_response_count?: number;
  patient_device_reading_count?: number;
  rpm_codes?: string;
  data_day_count?: number;
  rpm_cycle_start?: string;
  rpm_cycle_end?: string;
}

interface BackendReportResponse {
  columns: any[];
  csv: string;
  records: BackendReportRecord[];
}

// Frontend display format
interface MonthlyReport {
  total_minutes: number;
  total_patients: number;
  // PHA-629: staff vs patient activity aggregates.
  total_staff_activities: number;
  total_patient_activities: number;
  total_survey_responses: number;
  total_device_readings: number;
}

// PHA-629: one row per patient-side engagement event. The row set is the
// un-aggregated form of the same signals that produce the Patient Activities
// column, so for a given patient + range the row count equals that column.
interface PatientActivityRecord {
  Patient: string;
  Type: 'Survey' | 'Reading';
  Timestamp: string;
  Detail: string | null;
}

interface PatientActivityResponse {
  records: PatientActivityRecord[];
  columns?: any[];
  total_activities?: number;
  total_surveys?: number;
  total_readings?: number;
  counts_by_patient_month?: { [patientMonth: string]: number };
}

// Action report format (aligned with backend response)
interface ActionReportRecord {
  Action: string;
  // PHA-891 (addi, 2026-09-01): repetition counts for a merged row, e.g.
  // "Active Care Management Time ×2, Smart Hub Acknowledgement ×5" — undefined/
  // null for a row that never merged (nothing to detail).
  Details?: string | null;
  'Allocated time': number | null;
  'Begin timestamp': string;
  'End timestamp': string | null;
  Patient: string;
  'User (email)': string;
  'spent (minutes)': number;
}

// PHA-891: the detailed report response now carries a reconciled total computed
// in Python (per-(patient, month) round-then-sum, matching the Billing Summary),
// so the UI renders this instead of summing the visible — already rounded — rows.
interface ActionReportResponse {
  records: ActionReportRecord[];
  columns?: any[];
  csv?: string;
  total_minutes?: number;
  subtotals_by_patient_month?: { [patientMonth: string]: number };
}

// Patient option for Autocomplete
interface PatientOption {
  patient_id: string;
  first_name: string;
  last_name: string;
  label: string;
}

interface OrganizationOption {
  id: string;
  name: string;
}

// Helper functions to normalize action display names for audit compliance.
// PHA-891 (addi, 2026-09-01): a collapsed row's Action is now at most TWO
// lines — the event that determines the time (line 1) and every OTHER
// contributing action, bare names only (line 2) — joined by "\n" from the
// backend. Repetition counts no longer live here at all; they are the
// separate Details column. No action name contains a comma, so splitting
// each line on ', ' is unambiguous.
const splitActionLines = (action: string): string[] => String(action ?? '').split('\n');
const normalizeActionName = (name: string): string =>
  name === 'dwell in patient record' ? 'Active Care Management Time' : name;
const getDisplayActionName = (action: string): string =>
  splitActionLines(action)
    .map((line) => line.split(', ').map(normalizeActionName).join(', '))
    .join('\n');
// The bare dominant name only — for surfaces (Chip, PDF) that show one line.
const getDominantActionName = (action: string): string =>
  getDisplayActionName(action).split('\n')[0] ?? '';
// The second line only (ancillary actions), or '' when the row never merged.
const getAncillaryActionNames = (action: string): string =>
  getDisplayActionName(action).split('\n')[1] ?? '';

const BillingReport: React.FC = () => {
  const { user } = useAuth();
  // PHA-1111: the clinic picker is the SHARED session org, not page-local state. The BFF
  // overwrites `organization_id` on /session/patient/list_with_details with whatever
  // `req.session.currentOrganizationId` holds, so a page-local selection could never move
  // the patient list — only POST /session/organization/current (what setOrg does) can.
  const { orgId, orgList, setOrg, loading: orgLoading } = useOrganization();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [report, setReport] = useState<MonthlyReport | null>(null);
  const [csvData, setCsvData] = useState<string | null>(null);
  const [patients, setPatients] = useState<any[]>([]);
  const [actionReport, setActionReport] = useState<ActionReportRecord[] | null>(null);
  // PHA-891: backend-computed reconciled total for the detailed report (equals
  // the Billing Summary "Total Time"). Never re-derive by summing visible rows.
  const [actionReportTotal, setActionReportTotal] = useState<number | null>(null);
  const [loadingActions, setLoadingActions] = useState(false);
  const [summaryRecords, setSummaryRecords] = useState<BackendReportRecord[]>([]);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(50);

  // PHA-629: patient-side detail. Addi asked for the on-screen drilldown to
  // require picking a patient — device readings are high-volume (a daily BP cuff
  // plus a scale is 60+ rows/patient/month, so an org-wide month runs to 10k+),
  // and a silently truncated audit trail is the worst failure mode for billing
  // validation. The CSV export stays org-wide and complete; see exportDetailedCSV.
  const [patientActivity, setPatientActivity] = useState<PatientActivityRecord[] | null>(null);
  const [patientActivityTotals, setPatientActivityTotals] = useState<{
    total: number; surveys: number; readings: number;
  } | null>(null);
  const [activityPage, setActivityPage] = useState(0);
  const [activityRowsPerPage, setActivityRowsPerPage] = useState(50);
  const [exporting, setExporting] = useState(false);
  // PHA-1104: whether this patient's clinic can receive billing reports, and whether the
  // period on screen has already been filed. null until the status call answers, which
  // keeps the button HIDDEN rather than briefly offering a push we cannot perform.
  const [pushStatus, setPushStatus] = useState<{
    push_enabled: boolean;
    already_sent?: boolean;
    folder_name?: string | null;
    billing_period?: string | null;
  } | null>(null);
  const [pushingToEhr, setPushingToEhr] = useState(false);
  const [downloadingPdf, setDownloadingPdf] = useState(false);
  const [pushMessage, setPushMessage] = useState<{ text: string; severity: 'success' | 'error' } | null>(null);
  // PHA-1104: the range the report on screen was GENERATED for — not the two date inputs,
  // which the user can edit without pressing Generate. The PDF and the push are labelled
  // from this, so a filed document can never claim a period its contents do not cover.
  const [reportRange, setReportRange] = useState<{ from: string; to: string } | null>(null);
  // Backend-reconciled per-(patient, month) figures, keyed "<patient_id>|<YYYY-MM>". The
  // PDF's audit-trail total uses these rather than summing already-rounded visible rows,
  // for the same reason the on-screen total does (PHA-891).
  const [actionSubtotals, setActionSubtotals] = useState<Record<string, number>>({});
  // Its own flag, not loadingActions: the two detail fetches run concurrently, so
  // sharing one would let this table flash "No patient activities found" as soon
  // as the (unrelated) action report resolved.
  const [loadingActivity, setLoadingActivity] = useState(false);

  const hasBillableCodes = useMemo(() =>
    summaryRecords.some(r =>
      (r.cpt_codes && r.cpt_codes.trim() !== '') ||
      (r.rpm_codes && r.rpm_codes.trim() !== '')
    ),
    [summaryRecords]
  );

  // PHA-1111: the picker renders the shared org list; the selected clinic lives in
  // OrganizationContext (and therefore in the BFF session), so it is the same clinic the
  // rest of the app is on and it survives a page reload.
  const selectedOrganization: OrganizationOption | null = useMemo(
    () => orgList.find((o) => o.id === orgId) ?? null,
    [orgList, orgId],
  );

  // Filters
  const [selectedPatient, setSelectedPatient] = useState<string>('all');
  const [fromDate, setFromDate] = useState<string>(
    format(new Date(new Date().getFullYear(), new Date().getMonth() - 1, 1), 'yyyy-MM-dd')
  );
  const [toDate, setToDate] = useState<string>(
    format(new Date(new Date().getFullYear(), new Date().getMonth(), 0), 'yyyy-MM-dd')
  );

  // PHA-1168: clinic-designed billing views. Phanova Standard is everything below,
  // unchanged. A custom view renders only on "Generate Report", like the standard one.
  const customViews = useCustomViews(orgId, 'billing');
  const { setChoice: setViewChoice, activeViewId } = useViewChoice(
    orgId, 'billing', customViews.views, customViews.enabled,
  );
  const activeView = customViews.views.find((v) => v.id === activeViewId) || null;
  const [customGeneration, setCustomGeneration] = useState<{ n: number; from: string; to: string } | null>(null);
  useEffect(() => { setCustomGeneration(null); }, [activeViewId, orgId]);

  // PHA-629: the two detail tables render only for a specific patient. '' is the
  // transient value while the Autocomplete is open, so treat it as "not selected"
  // alongside the 'all' sentinel.
  const isPatientSelected = selectedPatient !== 'all' && selectedPatient !== '';

  const handleFromDateChange = (newFromDate: string) => {
    setFromDate(newFromDate);
    const parsed = new Date(newFromDate + 'T00:00:00');
    if (!isNaN(parsed.getTime())) {
      setToDate(format(lastDayOfMonth(parsed), 'yyyy-MM-dd'));
    }
  };

  // Patient options for Autocomplete
  const patientOptions: PatientOption[] = useMemo(() => {
    const allPatientsOption: PatientOption = {
      patient_id: 'all',
      first_name: 'All',
      last_name: 'Patients',
      label: 'All Patients'
    };

    const patientOpts = patients.map((p) => ({
      ...p,
      label: `${p.first_name} ${p.last_name} (${p.patient_id})`
    }));

    return [allPatientsOption, ...patientOpts];
  }, [patients]);

  // PHA-1111: the patient picker is re-fetched for the SELECTED clinic, not once on mount.
  // It previously called patientApi.list(), which the BFF resolves against the org held in
  // its own session — so the dropdown showed whatever clinic the user happened to be on
  // before navigating here, and changing the picker never touched it.
  //
  // `status: 'onboarded'` is the same server-side filter the patient grid's default view
  // uses (an active care plan). Billing only ever applies to onboarded patients, so a
  // half-onboarded patient in the dropdown is an offer the report cannot fill.
  //
  // No page_size: listWithDetails returns the whole visible set when paging is omitted,
  // which is what a dropdown needs — a silently truncated picker is worse than the bug
  // being fixed. `lean` drops the survey/answer blobs the dropdown never reads.
  //
  // Keyed on the org the PICKER is showing, not the raw context id: if the two ever
  // disagree (an id absent from the accessible list), a picker reading "no clinic" must
  // not be sitting on some other clinic's patients.
  const pickerOrgId = selectedOrganization?.id ?? null;
  useEffect(() => {
    if (orgLoading || !pickerOrgId) {
      setPatients([]);
      return;
    }
    const controller = new AbortController();
    (async () => {
      try {
        const response = await patientApi.listWithDetails(controller.signal, pickerOrgId, {
          lean: true,
          filters: { status: 'onboarded' },
        });
        if (controller.signal.aborted) return;
        setPatients(Array.isArray(response?.records) ? response.records : []);
      } catch (err: any) {
        // An abort is the expected outcome of switching clinics twice in quick
        // succession; only a real failure should empty the picker.
        if (controller.signal.aborted || err?.name === 'AbortError') return;
        console.error('Failed to load patients:', err);
        setPatients([]);
      }
    })();
    return () => controller.abort();
  }, [pickerOrgId, orgLoading]);

  // PHA-1111: everything on this page describes ONE clinic. When the clinic changes, a
  // patient id from the previous one is no longer selectable and a report already on
  // screen no longer describes what the picker says it does — so drop both rather than
  // leaving a stale report under a new clinic name.
  const handleOrganizationChange = async (next: OrganizationOption | null) => {
    if (!next || next.id === orgId) return;
    setSelectedPatient('all');
    setReport(null);
    setSummaryRecords([]);
    setCsvData(null);
    setActionReport(null);
    setActionReportTotal(null);
    setActionSubtotals({});
    setPatientActivity(null);
    setPatientActivityTotals(null);
    setPushStatus(null);
    setPushMessage(null);
    setReportRange(null);
    setError(null);
    try {
      await setOrg(next.id);
    } catch (err: any) {
      console.error('Failed to switch organization:', err);
      setError('Could not switch clinic. Please try again.');
    }
  };

  // PHA-629: both detail tables are per-patient, so they load together and only
  // when a patient is selected. Previously the action report fetched org-wide on
  // every Generate; that cost is gone for the 'All Patients' case.
  const loadDetailReports = async () => {
    if (!isPatientSelected) {
      setActionReport(null);
      setActionReportTotal(null);
      setActionSubtotals({});
      setPatientActivity(null);
      setPatientActivityTotals(null);
      // PHA-1104: the push is single-patient only, so drop the status the moment the report
      // is org-wide. A stale `push_enabled` would leave a button on screen that cannot
      // describe what it would file.
      setPushStatus(null);
      return;
    }
    await Promise.all([loadActionReport(), loadPatientActivityReport(), loadPushStatus()]);
  };

  // PHA-1104: whether this patient's clinic accepts billing reports, and whether the period
  // just generated is already filed. Failures leave the status null (button hidden) rather
  // than surfacing an error — the push is an enhancement to this page, not the page.
  const loadPushStatus = async (range?: { from: string; to: string }) => {
    // `range` is the period the report on screen describes. It is passed explicitly on a
    // refresh-after-push, because by then the user may have edited the date inputs and
    // `already_sent` would come back for a period nobody filed.
    const from = range?.from ?? fromDate;
    const to = range?.to ?? toDate;
    try {
      setPushStatus(
        await billingApi.getBillingReportEhrPushStatus(selectedPatient, from, to),
      );
    } catch (err: any) {
      console.error('PHA-1104: billing report EHR push status fetch failed:', err);
      setPushStatus(null);
    }
  };

  const loadPatientActivityReport = async () => {
    setLoadingActivity(true);
    try {
      const response: PatientActivityResponse = await billingApi.getPatientActivityReport(
        selectedPatient,
        selectedOrganization?.id || null,
        fromDate,
        toDate,
      );
      const records = Array.isArray(response?.records) ? response.records : [];
      setPatientActivity(records);
      // Prefer the backend totals; they are computed on the SQL-derived month, the
      // same one billing buckets on. Fall back to row counts only if absent.
      setPatientActivityTotals({
        total: typeof response?.total_activities === 'number' ? response.total_activities : records.length,
        surveys: typeof response?.total_surveys === 'number'
          ? response.total_surveys
          : records.filter(r => r.Type === 'Survey').length,
        readings: typeof response?.total_readings === 'number'
          ? response.total_readings
          : records.filter(r => r.Type === 'Reading').length,
      });
      setActivityPage(0);
    } catch (err: any) {
      console.error('❌ Failed to load patient activity report:', err);
      setPatientActivity([]);
      setPatientActivityTotals(null);
    } finally {
      setLoadingActivity(false);
    }
  };

  const loadActionReport = async () => {
    setLoadingActions(true);
    try {
      const patientFilter = selectedPatient === 'all' ? null : selectedPatient;
      console.log('🔍 Loading action report for patient:', patientFilter);

      // PHA-891: pass the date range to the backend. The detail rows now
      // interval-union overlapping events per (patient, month); date filtering
      // must happen at row selection (on begin_ts, before the union) — the same
      // point billing filters — so we no longer filter client-side by a merged
      // row's union start (which could wrongly drop it).
      const response: ActionReportResponse = await billingApi.getActionReport(
        patientFilter,
        selectedOrganization?.id || null,
        fromDate,
        toDate,
      );
      console.log('✅ Action report response:', response);

      // Handle different response formats
      let actions: ActionReportRecord[] = [];
      if (Array.isArray(response)) {
        actions = response;
      } else if (response.records && Array.isArray(response.records)) {
        actions = response.records;
      } else if ((response as any).actions && Array.isArray((response as any).actions)) {
        actions = (response as any).actions;
      } else {
        console.warn('Unknown action report format:', response);
        actions = [];
      }

      setActionReport(actions);
      // Reconciled total from the backend (per-(patient, month) round-then-sum).
      setActionReportTotal(
        typeof response.total_minutes === 'number' ? response.total_minutes : null,
      );
      setActionSubtotals(response.subtotals_by_patient_month ?? {});
      setPage(0); // Reset to first page when data changes
    } catch (err: any) {
      console.error('❌ Failed to load action report:', err);
      setActionReport([]);
      setActionReportTotal(null);
      setActionSubtotals({});
    } finally {
      setLoadingActions(false);
    }
  };

  const loadReport = async () => {
    setLoading(true);
    setError(null);
    
    try {
      // Try to call the real backend endpoint
      const patientFilter = selectedPatient === 'all' ? null : selectedPatient;
      console.log('🔍 Attempting to load billing report from backend...');
      console.log('Patient filter:', patientFilter);
      console.log('Date range:', fromDate, 'to', toDate);
      
      const response: BackendReportResponse = await billingApi.getMonthlyReport(patientFilter, fromDate, toDate, selectedOrganization?.id || null);
      console.log('✅ Backend response received:', response);
      // PHA-1104: this run's range is now the one the report describes, and any notice from
      // a previous push refers to a report that is no longer on screen.
      setReportRange({ from: fromDate, to: toDate });
      setPushMessage(null);
      
      // Store CSV data if provided
      if (response.csv) {
        setCsvData(response.csv);
      }
      
      // Transform backend response to frontend format
      if (response.records && Array.isArray(response.records)) {
        console.log('📅 Backend returned successfully', response.records.length, 'records');
        
        // Note: Backend returns ALL records, so we need to filter by date range on the client
        // Backend does not support date filtering yet
        const fromDateTime = parse(`${fromDate} 00:00:00`, 'yyyy-MM-dd HH:mm:ss', new Date()).getTime();
        const toDateTime = parse(`${toDate} 23:59:59`, 'yyyy-MM-dd HH:mm:ss', new Date()).getTime();

        const filteredRecords = response.records.filter(record => {
          // Parse the Month field (format: "2025-12", "2025-11", etc. - YYYY-MM from backend)
          try {
            // Parse the month (e.g., "2025-12") and get boundaries in local time
            const monthDate = parse(record.Month, 'yyyy-MM', new Date());
            const lastDay = format(lastDayOfMonth(monthDate), 'dd');

            const monthStart = parse(`${record.Month}-01 00:00:00`, 'yyyy-MM-dd HH:mm:ss', new Date()).getTime();
            const monthEnd = parse(`${record.Month}-${lastDay} 23:59:59`, 'yyyy-MM-dd HH:mm:ss', new Date()).getTime();

            // Check if this month overlaps with our date range
            return monthEnd >= fromDateTime && monthStart <= toDateTime;
          } catch (e) {
            console.warn('Failed to parse month:', record.Month, e);
            return false;
          }
        });
        
        console.log(`✅ Filtered ${response.records.length} records down to ${filteredRecords.length} records within date range`, filteredRecords);

        // Store summary records for export
        setSummaryRecords(filteredRecords);

        // Calculate totals from filtered records
        const totalMinutes = filteredRecords.reduce((sum, r) => sum + r['spent (minutes)'], 0);
        const uniquePatients = new Set(filteredRecords.map(r => r.patient_id));

        // PHA-629: staff vs patient activity totals.
        // Staff activities is sum(actions) — the real count of logged staff actions.
        // It is NOT activities.length, which counts synthetic per-patient-month rows
        // built below and would make the staff/patient comparison meaningless.
        const totalStaffActivities = filteredRecords.reduce((sum, r) => sum + (r.actions || 0), 0);
        const totalSurveyResponses = filteredRecords.reduce(
          (sum, r) => sum + (r.patient_survey_response_count || 0), 0);
        const totalDeviceReadings = filteredRecords.reduce(
          (sum, r) => sum + (r.patient_device_reading_count || 0), 0);
        const totalPatientActivities = totalSurveyResponses + totalDeviceReadings;
        
        // PHA-629: the synthetic per-patient-month "activities" rows and the
        // by_patient map that fed the old middle table are gone. That table
        // restated the summary's own numbers as fake action rows ("12 activities
        // / 3 user(s)") and carried no information the Billing Summary above it
        // did not already show; the real drilldown is the two detail tables below.
        const transformedReport: MonthlyReport = {
          total_minutes: totalMinutes,
          total_patients: uniquePatients.size,
          total_staff_activities: totalStaffActivities,
          total_patient_activities: totalPatientActivities,
          total_survey_responses: totalSurveyResponses,
          total_device_readings: totalDeviceReadings,
        };

        console.log('📊 Transformed report:', transformedReport);
        setReport(transformedReport);
      } else {
        throw new Error('Invalid response format from backend');
      }
      
      // Also load the per-patient detail reports (no-op unless a patient is picked)
      await loadDetailReports();

    } catch (err: any) {
      console.error('❌ Failed to load billing report:', err);
      setError(err.message || 'Failed to load billing report');
    } finally {
      setLoading(false);
    }
  };

  const formatDateTime = (dateString: string) => {
    try {
      return format(new Date(dateString), 'MMM dd, yyyy h:mm a');
    } catch {
      return dateString;
    }
  };

  // Format a YYYY-MM string to "MMM yyyy" without timezone conversion
  const formatMonthString = (monthStr: string) => {
    try {
      const [year, month] = monthStr.split('-');
      const date = new Date(parseInt(year), parseInt(month) - 1, 15);
      return format(date, 'MMM yyyy');
    } catch {
      return monthStr;
    }
  };

  // PHA-566 bug-list-2 #6: render as "4h 4m", never "4.1h".
  // Decimal hours are genuinely ambiguous — "4.1h" (4.1 HOURS = 246 min) was read
  // as "4:01" (241 min) and filed as an adoption-report mismatch. The two reports
  // had agreed all along. Whole minutes remove the ambiguity entirely.
  const formatDuration = (minutes: number) => {
    const safe = Number.isFinite(minutes) ? minutes : 0;
    const whole = Math.round(safe);
    if (whole < 60) {
      return `${whole}m`;
    }
    return `${Math.floor(whole / 60)}h ${whole % 60}m`;
  };

  // Shared helper to download CSV content
  const downloadCSV = (csv: string, filename: string) => {
    // PHA-891: lead with a UTF-8 BOM, otherwise Excel decodes the file as the
    // system codepage and mangles every non-ASCII character — the "×N" action
    // counts, and any accented patient name or note.
    const blob = new Blob(['\uFEFF' + csv], { type: 'text/csv;charset=utf-8' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    a.click();
  };

  // Date/time formatters for CSV export
  const formatDatePart = (timestamp?: string | null) => {
    if (!timestamp) return '';
    try {
      return format(new Date(timestamp), 'yyyy-MM-dd');
    } catch {
      return '';
    }
  };

  const formatTimePart = (timestamp?: string | null) => {
    if (!timestamp) return '';
    try {
      return format(new Date(timestamp), 'h:mm a');
    } catch {
      return '';
    }
  };

  const exportSummaryCSV = () => {
    // Filter to patients with any billable codes (CCM/APCM or RPM)
    const billableRecords = summaryRecords.filter(r =>
      (r.cpt_codes && r.cpt_codes.trim() !== '') ||
      (r.rpm_codes && r.rpm_codes.trim() !== '')
    );
    if (billableRecords.length === 0) return;

    // CSV headers with restructured columns (PHA-487)
    // PHA-629: Patient Activities column sits between Spent (minutes) and Data Days.
    let csv = 'Month,MRN,Full Name,DOB,ICD-10 Codes,Care Plan Created,Consent Date,Program,Care Mgmt CPT Codes,RPM Codes,Spent (minutes),Patient Activities,Data Days,RPM Service Start,RPM Service End\n';
    billableRecords.forEach(record => {
      const fullName = `${record.first_name || ''} ${record.last_name || ''}`.trim();
      const escapedIcd10 = record.icd10_codes ? `"${record.icd10_codes}"` : '';
      const escapedCpt = record.cpt_codes ? `"${record.cpt_codes}"` : '';
      const escapedName = fullName ? `"${fullName}"` : '';
      const escapedRpm = record.rpm_codes ? `"${record.rpm_codes}"` : '';
      const program = record.program || 'CCM-Only';
      const patientActivities =
        (record.patient_survey_response_count || 0) + (record.patient_device_reading_count || 0);

      csv += `${record.Month},"=""${record.patient_id}""",${escapedName},${record.date_of_birth || ''},${escapedIcd10},${record.care_plan_created_date || ''},${record.consent_date || ''},"${program}",${escapedCpt},${escapedRpm},${record['spent (minutes)']},${patientActivities},${record.data_day_count || ''},${record.rpm_cycle_start || ''},${record.rpm_cycle_end || ''}\n`;
    });

    const orgPrefix = selectedOrganization?.name ? `${selectedOrganization.name}-` : '';
    downloadCSV(csv, `${orgPrefix}billing-summary-${fromDate}-to-${toDate}.csv`);
  };

  // Escape a value for a quoted CSV field (doubling any embedded quote).
  const csvQuote = (value: unknown) => `"${String(value ?? '').replace(/"/g, '""')}"`;

  // PHA-629: the detailed export is deliberately ORG-WIDE and untruncated, even
  // when the screen is drilled into one patient — Addi: "the detailed CSV should
  // have them all without truncation". So it re-fetches with patient_id = null at
  // click time rather than serialising whatever the (per-patient) tables hold.
  // Care-team actions and patient activities land in ONE file, sorted so each
  // patient's rows sit together in time order.
  const exportDetailedCSV = async () => {
    setExporting(true);
    try {
      // Named to avoid shadowing the context's orgId, which is the same value.
      const exportOrgId = selectedOrganization?.id || null;
      const [actions, activity]: [ActionReportResponse, PatientActivityResponse] = await Promise.all([
        billingApi.getActionReport(null, exportOrgId, fromDate, toDate),
        billingApi.getPatientActivityReport(null, exportOrgId, fromDate, toDate),
      ]);

      const actionRows = Array.isArray(actions?.records) ? actions.records : [];
      const activityRows = Array.isArray(activity?.records) ? activity.records : [];

      // Unified row shape. `sortTs` is for ordering only and never printed, so
      // parsing it in the browser cannot shift any reported month — the billing
      // months come from the backend, which derives them in the DB session TZ.
      type DetailCsvRow = {
        patient: string; sortTs: number; cells: string[];
      };
      const rows: DetailCsvRow[] = [];

      // PHA-891: Action carries at most two lines (the dominant event, then any
      // ancillary ones) for a collapsed row, so the separate Members / Member
      // actions columns are gone. The total is the backend-reconciled figure —
      // never a sum of already-rounded per-row values. The repetition-count
      // breakdown rides in the "Detail" column below (blank for activity rows'
      // own use of that column is avoided since this is a care-team row).
      actionRows.forEach(action => {
        rows.push({
          patient: String(action.Patient ?? ''),
          sortTs: new Date(action['Begin timestamp']).getTime() || 0,
          cells: [
            `"=""${action.Patient}"""`,
            csvQuote(getDisplayActionName(action.Action)),
            csvQuote(action['User (email)'] ?? ''),
            formatDatePart(action['Begin timestamp']),
            formatTimePart(action['Begin timestamp']),
            formatDatePart(action['End timestamp']),
            formatTimePart(action['End timestamp']),
            String(action['spent (minutes)'] ?? ''),
            String(action['Allocated time'] ?? ''),
            'Care Team',
            csvQuote(action.Details ?? ''),
          ],
        });
      });

      // Patient rows carry no user, no end timestamp and no minutes — they are
      // point-in-time events, not time-tracked work. Those columns stay blank
      // rather than zero so they never read as "0 minutes of care".
      activityRows.forEach(act => {
        rows.push({
          patient: String(act.Patient ?? ''),
          sortTs: new Date(act.Timestamp).getTime() || 0,
          cells: [
            `"=""${act.Patient}"""`,
            csvQuote(act.Type === 'Survey' ? 'Survey response' : 'Device reading'),
            '', formatDatePart(act.Timestamp), formatTimePart(act.Timestamp),
            '', '', '', '',
            'Patient',
            csvQuote(act.Detail ?? ''),
          ],
        });
      });

      rows.sort((a, b) =>
        a.patient.localeCompare(b.patient) || a.sortTs - b.sortTs);

      // Existing 9 columns keep their order; Source and Detail are appended so
      // anything already parsing this file by position keeps working.
      let csv = 'Patient,Action,User (email),Begin date,Begin time,End date,End time,'
        + 'Spent (minutes),Allocated time,Source,Detail\n';
      rows.forEach(r => { csv += r.cells.join(',') + '\n'; });

      // Commas are positional padding: minutes land in "Spent (minutes)" (col 8),
      // and the patient-activity count rides in "Detail" (col 11).
      const totalMinutes = typeof actions?.total_minutes === 'number' ? actions.total_minutes : null;
      const totalActivities = typeof activity?.total_activities === 'number'
        ? activity.total_activities
        : activityRows.length;
      csv += `Total,,,,,,,${totalMinutes !== null ? totalMinutes.toFixed(1) : ''},,,`
        + `${totalActivities} patient activities\n`;

      const orgPrefix = selectedOrganization?.name ? `${selectedOrganization.name}-` : '';
      downloadCSV(csv, `${orgPrefix}billing-details-${fromDate}-to-${toDate}.csv`);
    } catch (err: any) {
      console.error('❌ Failed to export detailed CSV:', err);
      setError(err.message || 'Failed to export detailed report');
    } finally {
      setExporting(false);
    }
  };

  // PHA-1104: the summary rows this report is about — one per month for the selected
  // patient. `summaryRecords` is already server-filtered to that patient, but the filter is
  // re-asserted here because these rows become a document in a medical record.
  const pushableSummaries = useMemo(
    () => (isPatientSelected
      ? summaryRecords
        .filter(r => String(r.patient_id) === String(selectedPatient))
        // Billable months only — the SAME filter exportSummaryCSV applies. Across a range
        // where one month has no codes, including it would put a section with nothing to
        // attest to into a document whose only purpose is to attest to something.
        .filter(r =>
          (r.cpt_codes && r.cpt_codes.trim() !== '')
          || (r.rpm_codes && r.rpm_codes.trim() !== ''))
        .slice()
        .sort((a, b) => String(a.Month).localeCompare(String(b.Month)))
      : []),
    [summaryRecords, selectedPatient, isPatientSelected],
  );

  // The button appears only for a generated SINGLE-patient report that has something to
  // attest to. `hasBillableCodes` is the same gate Export Summary CSV uses: a report with no
  // billed codes has no claim to support, and filing one into a chart is noise.
  // Everything the PDF needs to exist: one patient, a generated report, and something
  // billed to attest to. `hasBillableCodes` is the same gate Export Summary CSV uses.
  const canBuildBillingReportPdf = Boolean(
    isPatientSelected
    && reportRange
    && pushableSummaries.length > 0
    && hasBillableCodes,
  );

  const canPushBillingReport = Boolean(
    // Everything Download needs, PLUS the clinic actually being configured to receive it.
    canBuildBillingReportPdf && pushStatus?.push_enabled,
  );

  // PHA-1116: a period can hold hours of logged care time and still award no code —
  // the PHA-723 CCM gate suppresses 99490 below 2 qualifying diagnoses, and the 20-minute
  // threshold suppresses it below that. When that happens Export Summary CSV greys out and
  // the EHR push button is not rendered at all, with nothing on screen to explain either.
  // A biller reads that as "I lack permission" (it is what PHA-1116 was filed as) rather
  // than as a billing outcome, so say it once, plainly, where the buttons are.
  const noBillableCodes = Boolean(report && summaryRecords.length > 0 && !hasBillableCodes);

  // Shown both in the notice and on the disabled CSV button, so the reason is reachable
  // whether the user reads the row or hovers the control.
  const NO_BILLABLE_CODES_REASON =
    'No billable codes in this period, so there is no claim to export or file.';

  /**
   * Builds the combined PDF from what is already on screen.
   *
   * ONE builder, used by both Download and Push — the file a biller inspects is exactly the
   * document that gets filed. Two builders would drift, and the drift would only ever be
   * noticed after the wrong thing was already in someone's chart.
   *
   * The audit-trail rows are the SAME union the Export Details CSV emits — care-team
   * actions plus patient-side events, separated by `source`.
   */
  const buildBillingReportPdf = async (): Promise<Blob | null> => {
    if (!reportRange || pushableSummaries.length === 0) return null;
    {
      // Month attribution. With a single month there is nothing to derive: every row
      // belongs to it. Across a range the row's own timestamp decides, which is what the
      // on-screen tables already show — the reconciling per-month TOTALS still come from
      // the backend below, so a boundary row landing in the neighbouring section cannot
      // change any number this document attests to.
      const soleMonth = pushableSummaries.length === 1 ? pushableSummaries[0].Month : null;
      const monthOf = (timestamp: string): string => {
        if (soleMonth) return soleMonth;
        const d = new Date(timestamp);
        return Number.isNaN(d.getTime()) ? '' : format(d, 'yyyy-MM');
      };

      const activities: BillingReportPdfActivity[] = [
        ...(actionReport ?? []).map(action => ({
          month: monthOf(action['Begin timestamp']),
          timestamp: action['Begin timestamp'],
          action: getDisplayActionName(action.Action),
          performedBy: action['User (email)'] ?? '',
          durationMinutes: typeof action['spent (minutes)'] === 'number'
            ? action['spent (minutes)']
            : null,
          source: 'Care Team' as const,
          detail: action.Details ?? '',
        })),
        ...(patientActivity ?? []).map(act => ({
          month: monthOf(act.Timestamp),
          timestamp: act.Timestamp,
          action: act.Type === 'Survey' ? 'Survey response' : 'Device reading',
          // PHA-1113 #5: the patient performed this — answered the survey, took the
          // reading. "System/Patient" named the recorder as much as the actor and read, in
          // a billing audit trail, as though Phanova might have supplied the answer.
          performedBy: 'Patient',
          // null, never 0 — a point-in-time event is not zero minutes of care.
          durationMinutes: null,
          source: 'Patient' as const,
          detail: act.Detail ?? '',
        })),
      ];

      const blob = await generateBillingReportPdf({
        summaries: pushableSummaries.map(r => ({
          month: r.Month,
          patientId: r.patient_id,
          patientName: `${r.first_name || ''} ${r.last_name || ''}`.trim(),
          dateOfBirth: r.date_of_birth,
          icd10Codes: r.icd10_codes,
          program: r.program || 'CCM-Only',
          carePlanCreated: r.care_plan_created_date,
          consentDate: r.consent_date,
          cptCodes: r.cpt_codes,
          rpmCodes: r.rpm_codes,
          // The billed figure is the summary row's own, always — the same value the
          // Billing Summary table and Export Summary CSV show. Sourcing it from the action
          // report instead would make the cell depend on whether that fetch succeeded, and
          // would differ outright on a partial-month range (the action rows are filtered to
          // the range; the summary row covers the whole billed month).
          spentMinutes: r['spent (minutes)'],
          // The audit trail's own total, reconciled by the backend. Never a sum of the
          // already-rounded detail rows — that is the PHA-891 mismatch.
          loggedMinutes: actionSubtotals[`${r.patient_id}|${r.Month}`] ?? null,
          patientActivities:
            (r.patient_survey_response_count || 0) + (r.patient_device_reading_count || 0),
        })),
        activities,
        fromDate: reportRange.from,
        toDate: reportRange.to,
        // Same-origin asset, so the PDF's fetch of it is never blocked. Phanova's mark, not
        // the clinic's — see the branding note in generateBillingReportPdf.
        logoUrl: '/logo192.png',
      });

      return blob;
    }
  };

  /**
   * PHA-1104 follow-up: save the report locally instead of filing it.
   *
   * Deliberately NOT gated on the clinic's EHR configuration — this touches no EHR, so
   * requiring one would be gating a local download on an unrelated integration. It IS
   * gated on the same billable-codes rule as the push and as Export Summary CSV: a report
   * with nothing billed has no claim to support.
   */
  const handleDownloadBillingReportPdf = async () => {
    if (!reportRange || pushableSummaries.length === 0) return;
    setDownloadingPdf(true);
    try {
      const blob = await buildBillingReportPdf();
      if (!blob) return;
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = billingReportPdfFilename(selectedPatient, reportRange.from, reportRange.to);
      a.click();
      // Release the object URL once the click has been dispatched; without this every
      // download leaks its blob for the lifetime of the page.
      window.URL.revokeObjectURL(url);
    } catch (err: any) {
      console.error('PHA-1104: billing report PDF download failed:', err);
      setPushMessage({
        text: err?.message || 'Failed to generate the billing report PDF',
        severity: 'error',
      });
    } finally {
      setDownloadingPdf(false);
    }
  };

  const handlePushBillingReportToEhr = async (force: boolean) => {
    if (!reportRange || pushableSummaries.length === 0) return;
    setPushingToEhr(true);
    try {
      const blob = await buildBillingReportPdf();
      if (!blob) return;

      await billingApi.pushBillingReportToEhr(
        selectedPatient,
        blob,
        billingReportPdfFilename(selectedPatient, reportRange.from, reportRange.to),
        reportRange.from,
        reportRange.to,
        force,
      );

      // "Sent", not "Filed": Cobalt has accepted the upload for processing, and only the
      // document.created webhook tells us the EHR actually filed it.
      setPushMessage({
        text: `Billing report sent to the EHR${pushStatus?.folder_name ? ` (${pushStatus.folder_name})` : ''}.`,
        severity: 'success',
      });
      await loadPushStatus(reportRange);
    } catch (err: any) {
      if (err?.alreadySent && !force) {
        // Not a failure — this period is already in the chart. Make the second copy an
        // explicit choice rather than something a double-click can cause.
        if (window.confirm(`${err.message}\n\nSend another copy to the EHR?`)) {
          await handlePushBillingReportToEhr(true);
          return;
        }
      } else {
        setPushMessage({
          text: err?.message || 'Failed to send the billing report to the EHR',
          severity: 'error',
        });
      }
    } finally {
      setPushingToEhr(false);
    }
  };

  return (
    <Box sx={{ p: 3, maxWidth: 1400, mx: 'auto' }}>
      {/* Header with Back Button */}
      <Stack direction="row" alignItems="center" spacing={2} sx={{ mb: 3 }}>
        <IconButton 
          onClick={() => navigate('/')}
          sx={{ 
            bgcolor: 'primary.main', 
            color: 'white',
            '&:hover': { bgcolor: 'primary.dark' }
          }}
          aria-label="back to home"
        >
          <ArrowBack />
        </IconButton>
        <Typography variant="h4" sx={{ flex: 1 }}>
          CCM Billing Report
        </Typography>
        {customViews.enabled && (customViews.views.length > 0 || customViews.canDesign) && (
          <ViewSelector
            views={customViews.views}
            activeViewId={activeViewId}
            onChange={setViewChoice}
            onManage={customViews.canDesign ? () => navigate('/clinic-settings/views?surface=billing') : undefined}
          />
        )}
      </Stack>

      {/* PHA-1243: billing reports designed and listed here, not on the Reports page. */}
      <BillingReportsPanel orgId={orgId} />

      {/* Filters Card */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            Filters
          </Typography>
          <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
            <TextField
              label="From Date"
              type="date"
              value={fromDate}
              onChange={(e) => handleFromDateChange(e.target.value)}
              InputLabelProps={{ shrink: true }}
              sx={{ flex: 1 }}
            />
            <TextField
              label="To Date"
              type="date"
              value={toDate}
              onChange={(e) => setToDate(e.target.value)}
              InputLabelProps={{ shrink: true }}
              inputProps={{ min: fromDate }}
              sx={{ flex: 1 }}
            />
            <Autocomplete
              sx={{ flex: 1 }}
              options={patientOptions}
              getOptionLabel={(option) => option.label}
              value={patientOptions.find(p => p.patient_id === selectedPatient) || null}
              onChange={(_, newValue) => {
                setSelectedPatient(newValue?.patient_id || '');
              }}
              onClose={(_, reason) => {
                if (reason === 'blur' && !selectedPatient) {
                  setSelectedPatient('all');
                }
              }}
              isOptionEqualToValue={(option, value) => option.patient_id === value.patient_id}
              // PHA-1168: a custom view reports on the whole clinic; its own filters
              // narrow it, so the single-patient picker does not apply.
              disabled={!!activeView}
              renderInput={(params) => (
                <TextField {...params} label="Patient"
                  helperText={activeView ? 'Not used by custom views' : undefined} />
              )}
            />
            <Autocomplete
              sx={{ flex: 1 }}
              options={orgList}
              loading={orgLoading}
              getOptionLabel={(option) => option.name}
              value={selectedOrganization}
              onChange={(_, newValue) => { void handleOrganizationChange(newValue); }}
              isOptionEqualToValue={(option, value) => option.id === value.id}
              renderInput={(params) => (
                <TextField {...params} label="Organization" />
              )}
            />
            <Button
              variant="contained"
              onClick={activeView
                ? () => setCustomGeneration((g) => ({ n: (g?.n || 0) + 1, from: fromDate, to: toDate }))
                : loadReport}
              disabled={loading || !selectedOrganization}
              startIcon={loading ? <CircularProgress size={20} /> : <CalendarToday />}
              sx={{ minWidth: 150 }}
            >
              Generate Report
            </Button>
          </Stack>
        </CardContent>
      </Card>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      {/* PHA-1168: a selected custom view replaces the standard summary below. */}
      {activeView && customGeneration && (
        <CustomBillingView
          orgId={orgId}
          viewId={activeView.id}
          viewName={activeView.name}
          fromDate={customGeneration.from}
          toDate={customGeneration.to}
          generation={customGeneration.n}
          onUnavailable={(reason) => {
            setError(reason === 'stale'
              ? `"${activeView.name}" needs to be re-saved by a clinic admin. Showing Phanova Standard.`
              : `"${activeView.name}" is no longer available. Showing Phanova Standard.`);
            setViewChoice(STANDARD);
            customViews.refresh();
          }}
        />
      )}
      {activeView && !customGeneration && (
        <Paper sx={{ p: 4, textAlign: 'center' }}>
          <Typography color="text.secondary">
            Click "Generate Report" to run "{activeView.name}" for the selected dates
          </Typography>
        </Paper>
      )}

      {/* Summary Cards */}
      {!activeView && report && (
        <>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} sx={{ mb: 3 }}>
            <Card sx={{ flex: 1 }}>
              <CardContent>
                <Stack direction="row" spacing={2} alignItems="center">
                  <AccessTime color="primary" fontSize="large" />
                  <Box>
                    <Typography variant="h4" color="primary">
                      {formatDuration(report.total_minutes)}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Total Time
                    </Typography>
                  </Box>
                </Stack>
              </CardContent>
            </Card>
            
            <Card sx={{ flex: 1 }}>
              <CardContent>
                <Stack direction="row" spacing={2} alignItems="center">
                  <Person color="primary" fontSize="large" />
                  <Box>
                    <Typography variant="h4" color="primary">
                      {report.total_patients}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Patients
                    </Typography>
                  </Box>
                </Stack>
              </CardContent>
            </Card>
            
            <Card sx={{ flex: 1 }}>
              <CardContent>
                <Stack direction="row" spacing={2} alignItems="center">
                  <CalendarToday color="primary" fontSize="large" />
                  <Box>
                    <Typography variant="h4" color="primary">
                      {report.total_staff_activities}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Staff Activities
                    </Typography>
                  </Box>
                </Stack>
              </CardContent>
            </Card>

            {/* PHA-629: patient-side engagement, shown alongside staff activity so an
                APCM code with zero staff time can be traced to the patient activity
                that justified it. Surveys and device readings are peers in the
                has_activity gate, so both are counted. */}
            <Card sx={{ flex: 1 }}>
              <CardContent>
                <Stack direction="row" spacing={2} alignItems="center">
                  <Favorite color="primary" fontSize="large" />
                  <Box>
                    <Typography variant="h4" color="primary">
                      {report.total_patient_activities}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Patient Activities
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {report.total_survey_responses} surveys · {report.total_device_readings} readings
                    </Typography>
                  </Box>
                </Stack>
              </CardContent>
            </Card>
          </Stack>

          {/* PHA-1104: the push outcome. Inline rather than a toast — a biller needs to be
              able to look back at what happened to a document they filed. */}
          {pushMessage && (
            <Alert
              severity={pushMessage.severity}
              sx={{ mb: 2 }}
              onClose={() => setPushMessage(null)}
            >
              {pushMessage.text}
            </Alert>
          )}

          {/* PHA-1116: the only place that explains a greyed Export Summary CSV and an
              absent Push Billing Report to EMR button. Both follow from the report having
              no awarded code, which is invisible on a screen that is otherwise full of
              logged minutes — the ticket was filed as a permissions failure because of it. */}
          {noBillableCodes && (
            <Alert severity="info" sx={{ mb: 2 }}>
              {NO_BILLABLE_CODES_REASON} The care time below is recorded — it just has not
              met a code for these months. CCM 99490 needs at least 20 minutes
              <strong> and</strong> at least two qualifying diagnoses from the supported list,
              on a condition-specific (not general) care plan. APCM codes need an APCM tier,
              and RPM codes need device readings. Export Summary CSV and Push Billing Report
              to EMR both exist to support a claim, so they stay unavailable until a row
              carries a code.
            </Alert>
          )}

          {/* Export Buttons */}
          <Box sx={{ mb: 2, display: 'flex', justifyContent: 'flex-end', gap: 2 }}>
            {/* PHA-1104 follow-up: save the very same PDF locally. Needs no EHR
                configuration and files nothing, so a biller can read the report before
                deciding to send it — and QA can check it without touching a chart. */}
            {canBuildBillingReportPdf && (
              <Button
                variant="outlined"
                startIcon={downloadingPdf ? <CircularProgress size={20} /> : <PictureAsPdf />}
                onClick={handleDownloadBillingReportPdf}
                disabled={downloadingPdf}
              >
                {downloadingPdf ? 'Preparing…' : 'Download Report PDF'}
              </Button>
            )}
            {/* PHA-1104: only for a single-patient report whose clinic has billing-report
                push turned on (PHA-1105's setting). Placed to the LEFT of Export Summary
                CSV, per the ticket. */}
            {canPushBillingReport && (
              <Button
                variant="contained"
                color="secondary"
                startIcon={pushingToEhr ? <CircularProgress size={20} color="inherit" /> : <CloudUpload />}
                onClick={() => handlePushBillingReportToEhr(false)}
                disabled={pushingToEhr}
              >
                {pushingToEhr ? 'Sending…' : 'Push Billing Report to EMR'}
              </Button>
            )}
            {/* PHA-1116: a disabled MUI Button swallows pointer events, so the tooltip
                has to hang off a wrapping span or it never opens — which is exactly the
                state a user needs the explanation in. */}
            <Tooltip title={!hasBillableCodes ? NO_BILLABLE_CODES_REASON : ''}>
              <span>
                <Button
                  variant="contained"
                  startIcon={<Download />}
                  onClick={exportSummaryCSV}
                  disabled={!hasBillableCodes}
                >
                  Export Summary CSV
                </Button>
              </span>
            </Tooltip>
            {/* PHA-629: always exports the whole org, so it is enabled whenever a
                report exists — including while the screen is filtered to one
                patient. It fetches on click, hence the spinner. */}
            <Button
              variant="outlined"
              startIcon={exporting ? <CircularProgress size={20} /> : <Download />}
              onClick={exportDetailedCSV}
              disabled={!report || exporting}
            >
              {exporting ? 'Preparing…' : 'Export Details CSV'}
            </Button>
          </Box>

          {/* Billing Summary Table */}
          {summaryRecords.length > 0 && (
            <Paper sx={{ mb: 3 }}>
              <Box sx={{ p: 2 }}>
                <Typography variant="h6" gutterBottom>Billing Summary</Typography>
              </Box>
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell><strong>Month</strong></TableCell>
                      <TableCell><strong>Patient ID</strong></TableCell>
                      <TableCell><strong>Name</strong></TableCell>
                      <TableCell><strong>Program</strong></TableCell>
                      <TableCell><strong>Care Mgmt CPT Codes</strong></TableCell>
                      <TableCell><strong>RPM Codes</strong></TableCell>
                      <TableCell align="right"><strong>Minutes</strong></TableCell>
                      <TableCell align="right"><strong>Patient Activities</strong></TableCell>
                      <TableCell align="right"><strong>Data Days</strong></TableCell>
                      <TableCell><strong>RPM Service Start</strong></TableCell>
                      <TableCell><strong>RPM Service End</strong></TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {summaryRecords.map((record, index) => {
                      const program = record.program || 'CCM-Only';
                      const programColor = program.includes('Tier 2') ? 'secondary'
                                         : program.includes('Tier 1') ? 'primary'
                                         : 'default';
                      return (
                        <TableRow key={index} hover>
                          <TableCell>{formatMonthString(record.Month)}</TableCell>
                          <TableCell>{record.patient_id}</TableCell>
                          <TableCell>{`${record.first_name || ''} ${record.last_name || ''}`.trim()}</TableCell>
                          <TableCell>
                            <Chip label={program} size="small" color={programColor as any} variant="outlined" />
                          </TableCell>
                          <TableCell>
                            {record.cpt_codes ? (
                              <Chip label={record.cpt_codes} size="small" color="success" variant="outlined" />
                            ) : (
                              <Typography variant="caption" color="text.secondary">—</Typography>
                            )}
                          </TableCell>
                          <TableCell>
                            {record.rpm_codes ? (
                              <Chip label={record.rpm_codes} size="small" color="info" variant="outlined" />
                            ) : (
                              <Typography variant="caption" color="text.secondary">—</Typography>
                            )}
                          </TableCell>
                          <TableCell align="right">{record['spent (minutes)'].toFixed(1)}</TableCell>
                          <TableCell align="right">
                            <Tooltip
                              title={`${record.patient_survey_response_count || 0} survey response(s), ${record.patient_device_reading_count || 0} device reading(s)`}
                            >
                              <span>
                                {(record.patient_survey_response_count || 0) +
                                  (record.patient_device_reading_count || 0)}
                              </span>
                            </Tooltip>
                          </TableCell>
                          <TableCell align="right">{record.data_day_count || 0}</TableCell>
                          <TableCell>
                            <Typography variant="caption">{record.rpm_cycle_start || '—'}</Typography>
                          </TableCell>
                          <TableCell>
                            <Typography variant="caption">{record.rpm_cycle_end || '—'}</Typography>
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </TableContainer>
            </Paper>
          )}

          {/* PHA-629: the detail drilldown is per-patient. With "All Patients"
              selected there is nothing to show — the org-wide picture lives in
              the Billing Summary above and in the Export Details CSV, which
              stays complete and untruncated regardless of this filter. */}
          {!isPatientSelected && (
            <Paper sx={{ p: 4, mb: 3, textAlign: 'center' }}>
              <Typography variant="h6" gutterBottom>Detail Reports</Typography>
              <Typography color="text.secondary">
                Select a patient in the filters above to see their care team actions
                and patient activities.
              </Typography>
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
                Export Details CSV covers every patient in the organization regardless
                of this filter.
              </Typography>
            </Paper>
          )}

          {/* Care Team Actions (staff-side detail) */}
          {isPatientSelected && (
          <Paper sx={{ p: 2, mb: 3 }}>
            <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
              <Box>
                <Typography variant="h6">
                  Care Team Actions
                </Typography>
                {/* PHA-891: overlapping events are collapsed to the biggest time
                    range; this total is the backend-reconciled figure and equals
                    the Billing Summary "Total Time". */}
                {typeof actionReportTotal === 'number' && actionReport && actionReport.length > 0 && (
                  <Typography variant="body2" color="text.secondary">
                    Total: {formatDuration(actionReportTotal)}
                  </Typography>
                )}
              </Box>
              {loadingActions && <CircularProgress size={24} />}
            </Stack>
            <Divider sx={{ mb: 2 }} />
            
            {actionReport && actionReport.length > 0 ? (
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell><strong>Patient</strong></TableCell>
                      <TableCell><strong>Action</strong></TableCell>
                      <TableCell><strong>Details</strong></TableCell>
                      <TableCell><strong>User (Email)</strong></TableCell>
                      <TableCell><strong>Begin Timestamp</strong></TableCell>
                      <TableCell><strong>End Timestamp</strong></TableCell>
                      <TableCell align="right"><strong>Spent (Minutes)</strong></TableCell>
                      <TableCell align="right"><strong>Allocated Time</strong></TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {actionReport
                      .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                      .map((action, index) => {
                      // PHA-891 (addi, 2026-09-01): the event that determines the
                      // time is its own line; any OTHER contributing actions sit
                      // on a second line in the SAME cell, bare names only — their
                      // counts are the separate Details column, not '×N' suffixes
                      // crammed into this one.
                      const dominant = getDominantActionName(action.Action);
                      const ancillary = getAncillaryActionNames(action.Action);
                      return (
                        <TableRow key={page * rowsPerPage + index} hover>
                          <TableCell>{action.Patient}</TableCell>
                          <TableCell>
                            <Chip label={dominant} size="small" color="secondary" variant="outlined" />
                            {ancillary && (
                              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                                {ancillary}
                              </Typography>
                            )}
                          </TableCell>
                          <TableCell>
                            <Typography variant="caption" color="text.secondary">
                              {action.Details ?? ''}
                            </Typography>
                          </TableCell>
                          <TableCell>{action['User (email)']}</TableCell>
                          <TableCell>{formatDateTime(action['Begin timestamp'])}</TableCell>
                          <TableCell>
                            {action['End timestamp'] ? formatDateTime(action['End timestamp']) : (
                              <Chip label="Ongoing" size="small" color="info" />
                            )}
                          </TableCell>
                          <TableCell align="right">
                            <Typography variant="body2" sx={{ fontWeight: 'medium' }}>
                              {action['spent (minutes)'] > 0 ? formatDuration(action['spent (minutes)']) : '-'}
                            </Typography>
                          </TableCell>
                          <TableCell align="right">
                            <Typography variant="caption" color="text.secondary">
                              {action['Allocated time'] ? `${action['Allocated time']} min` : '-'}
                            </Typography>
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
                <TablePagination
                  rowsPerPageOptions={[25, 50, 100]}
                  component="div"
                  count={actionReport.length}
                  rowsPerPage={rowsPerPage}
                  page={page}
                  onPageChange={(event, newPage) => setPage(newPage)}
                  onRowsPerPageChange={(event) => {
                    setRowsPerPage(parseInt(event.target.value, 10));
                    setPage(0);
                  }}
                />
              </TableContainer>
            ) : (
              <Box sx={{ py: 4, textAlign: 'center' }}>
                <Typography color="text.secondary">
                  {loadingActions
                    ? 'Loading action report...'
                    : 'No care team actions found for the selected filters'}
                </Typography>
              </Box>
            )}
          </Paper>
          )}

          {/* PHA-629: Patient Activities detail — the events behind the Patient
              Activities column above. Surveys and device readings are peers in
              the has_activity gate that fires the APCM code, so both are listed:
              a reading-only patient must not read as "no patient activity" beside
              a billed code. The total is backend-computed and ties to this
              patient's Patient Activities value in the Billing Summary. */}
          {isPatientSelected && (
          <Paper sx={{ p: 2 }}>
            <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
              <Box>
                <Typography variant="h6">
                  Patient Activities
                </Typography>
                {patientActivityTotals && (
                  <Typography variant="body2" color="text.secondary">
                    Total: {patientActivityTotals.total} activities
                    {' '}({patientActivityTotals.surveys} surveys · {patientActivityTotals.readings} readings)
                  </Typography>
                )}
              </Box>
              {loadingActivity && <CircularProgress size={24} />}
            </Stack>
            <Divider sx={{ mb: 2 }} />

            {patientActivity && patientActivity.length > 0 ? (
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell><strong>Patient</strong></TableCell>
                      <TableCell><strong>Type</strong></TableCell>
                      <TableCell><strong>Timestamp</strong></TableCell>
                      <TableCell><strong>Detail</strong></TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {patientActivity
                      .slice(activityPage * activityRowsPerPage,
                             activityPage * activityRowsPerPage + activityRowsPerPage)
                      .map((act, index) => (
                        <TableRow key={activityPage * activityRowsPerPage + index} hover>
                          <TableCell>{act.Patient}</TableCell>
                          <TableCell>
                            <Chip
                              label={act.Type === 'Survey' ? 'Survey response' : 'Device reading'}
                              size="small"
                              color={act.Type === 'Survey' ? 'primary' : 'info'}
                              variant="outlined"
                            />
                          </TableCell>
                          <TableCell>{formatDateTime(act.Timestamp)}</TableCell>
                          <TableCell>
                            <Typography variant="caption" color="text.secondary">
                              {act.Detail || '—'}
                            </Typography>
                          </TableCell>
                        </TableRow>
                      ))}
                  </TableBody>
                </Table>
                <TablePagination
                  rowsPerPageOptions={[25, 50, 100]}
                  component="div"
                  count={patientActivity.length}
                  rowsPerPage={activityRowsPerPage}
                  page={activityPage}
                  onPageChange={(event, newPage) => setActivityPage(newPage)}
                  onRowsPerPageChange={(event) => {
                    setActivityRowsPerPage(parseInt(event.target.value, 10));
                    setActivityPage(0);
                  }}
                />
              </TableContainer>
            ) : (
              <Box sx={{ py: 4, textAlign: 'center' }}>
                <Typography color="text.secondary">
                  {loadingActivity
                    ? 'Loading patient activities...'
                    : 'No patient activities found for the selected filters'}
                </Typography>
              </Box>
            )}
          </Paper>
          )}
        </>
      )}

      {!activeView && !report && !loading && (
        <Paper sx={{ p: 4, textAlign: 'center' }}>
          <Typography color="text.secondary">
            Select filters and click "Generate Report" to view billing data
          </Typography>
        </Paper>
      )}
    </Box>
  );
};

export default BillingReport;
